import React, { useState } from 'react';
import { UserIcon, BuildingIcon, UploadIcon, CameraIcon, FileCheckIcon, CheckCircle2Icon } from 'lucide-react';
import { Card, StepHeading, BlockLabel, Field, TextArea, ChoiceCards, SimpleSelect, Helper } from '../SettingsUI';
import { ShopConfig, SellerType, BUSINESS_TYPE_OPTIONS } from '../../../data/shopConfig';
interface Props {
  config: ShopConfig;
  update: (patch: Partial<ShopConfig>) => void;
}
const slugify = (value: string) => value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
/** Handles taken by other merchants — drives the availability message. */
const TAKEN_HANDLES = ['rental', 'cars', 'dubai', 'talab'];
export function BusinessDetails({
  config,
  update
}: Props) {
  const [writing, setWriting] = useState(false);
  const isBusiness = config.sellerType === 'business';
  const licenseRead = config.verification.licenseNumber !== '';
  const handleAvailable = config.handle.length > 2 && !TAKEN_HANDLES.includes(config.handle);
  const readLicense = () => {
    update({
      legalName: 'Easylease Vehicle Rental LLC',
      businessType: 'Vehicle Rental',
      verification: {
        ...config.verification,
        license: 'pending',
        licenseNumber: 'CN-1094552',
        licenseExpiry: '14 Mar 2027'
      }
    });
  };
  const writeAbout = () => {
    setWriting(true);
    window.setTimeout(() => {
      update({
        about: `${config.publicName || 'Our business'} is a ${config.businessType.toLowerCase() || 'UAE'} provider serving customers across the UAE. We offer short-term and long-term options, delivery and collection, and support from a dedicated team — so customers always get the right choice quickly.`
      });
      setWriting(false);
    }, 900);
  };
  return <div className="space-y-5" data-id="element-3788" data-mp="lq6mnbypj812">
      <StepHeading title="Tell us about your business" desc="Just the basics for now — don't worry, you can change any of this later." data-id="element-3789" data-mp="lqgdf1rktj7t" />

      <Card data-id="element-3790" data-mp="749jwo9sgymj">
        <BlockLabel data-id="element-3791" data-mp="73rgn7oplk54">How are you selling?</BlockLabel>
        <ChoiceCards<SellerType> value={config.sellerType} onChange={sellerType => update({
        sellerType
      })} options={[{
        id: 'personal',
        label: 'Personal Seller',
        desc: 'Selling as an individual',
        icon: UserIcon
      }, {
        id: 'business',
        label: 'Business',
        desc: 'Registered company or shop',
        icon: BuildingIcon
      }]} data-id="element-3792" data-mp="c3m93fo4uhsa" />
      </Card>

      <Card className="space-y-5" data-id="element-3793" data-mp="h1ns60lk6h3r">
        <div data-id="element-3794" data-mp="h0mf5qkzfkje">
          <BlockLabel hint="Your customers will see this on your shop" data-id="element-3795" data-mp="h0w2t7ik1flx">
            {isBusiness ? 'Shop Logo' : 'Profile Photo'}
          </BlockLabel>
          <div className="flex items-center gap-4" data-id="element-3796" data-mp="19qm9ivhuxf80">
            <div className="w-20 h-20 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0" data-id="element-3797" data-mp="1ei4cfp3fxpvm">
              {config.logo ? <img src={config.logo} alt="" className="w-full h-full object-contain p-2" data-id="element-3798" data-mp="1nujvmkil7xi1" /> : <CameraIcon className="w-6 h-6 text-slate-300" data-id="element-3799" data-mp="1xly076pebni1" />}
            </div>
            <label className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 hover:border-primary-indigo/50 cursor-pointer transition-colors duration-150 ease-out" data-id="element-3800" data-mp="1soen4a4fpgro">
              <UploadIcon className="w-4 h-4" data-id="element-3801" data-mp="1evyjb73plepp" />
              Upload Logo
              <input type="file" accept="image/*" className="hidden" onChange={e => {
              const file = e.target.files?.[0];
              if (file) update({
                logo: URL.createObjectURL(file)
              });
            }} data-id="element-3802" data-mp="1etvh7opa7v4m" />
            </label>
          </div>
        </div>

        {isBusiness && <div data-id="element-3803" data-mp="ux1ag5iz87zr">
            <BlockLabel required data-id="element-3804" data-mp="uxjdbl059kgn">Business Name</BlockLabel>
            {licenseRead ? <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-success/[0.06] border border-success/20" data-id="element-3805" data-mp="zwduyjmdl2z6">
                <div className="min-w-0" data-id="element-3806" data-mp="18vhe1nljlido">
                  <p className="text-sm font-semibold text-dark-navy truncate" data-id="element-3807" data-mp="18wixoggmjd70">
                    {config.legalName}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5" data-id="element-3808" data-mp="1isvtpkzn634x">
                    Licence {config.verification.licenseNumber} · expires{' '}
                    {config.verification.licenseExpiry}
                  </p>
                </div>
                <span className="px-2 py-1 rounded-full bg-success/10 text-[10px] font-bold uppercase tracking-wider text-success flex-shrink-0" data-id="element-3809" data-mp="1shnhri1l03ak">
                  From trade licence
                </span>
              </div> : <div className="space-y-3" data-id="element-3810" data-mp="21hamacplxp7u">
                <button onClick={readLicense} className="w-full flex items-center justify-center gap-2 py-3.5 border border-dashed border-slate-300 rounded-xl text-sm font-semibold text-slate-600 hover:border-primary-indigo/50 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-3811" data-mp="3m2qqn9gm1wzz">
                  <FileCheckIcon className="w-4 h-4" data-id="element-3812" data-mp="384977nxqn6nv" />
                  Upload Trade License
                </button>
                <Helper data-id="element-3813" data-mp="4lfx5l464zyg">
                  Upload your trade licence and Talab can fill in your business
                  information automatically — legal name, licence number,
                  expiry and business type.
                </Helper>
              </div>}
          </div>}

        <Field label={isBusiness ? 'Public Shop Name' : 'Full Name'} value={config.publicName} onChange={publicName => update({
        publicName,
        handle: slugify(publicName) || config.handle
      })} placeholder="EasyLease" helper="This is the name customers see — it can be shorter than your legal name." required data-id="element-3814" data-mp="saif6u2psuth" />

        <div data-id="element-3815" data-mp="xn777kofakau">
          <BlockLabel data-id="element-3816" data-mp="xnqiiijkiafw">Shop URL</BlockLabel>
          <div className="flex items-stretch rounded-xl border border-slate-200 overflow-hidden focus-within:border-primary-indigo/50 focus-within:ring-4 focus-within:ring-primary-indigo/10 transition-[border-color,box-shadow] duration-150 ease-out" data-id="element-3817" data-mp="11pbzaez838vd">
            <span className="px-3.5 flex items-center bg-slate-50 border-r border-slate-200 text-sm text-slate-400 whitespace-nowrap" data-id="element-3818" data-mp="sq73hgf1ptmw">
              talab.app/shop/
            </span>
            <input value={config.handle} onChange={e => update({
            handle: slugify(e.target.value)
          })} className="flex-1 min-w-0 px-3 py-2.5 text-sm font-semibold text-slate-800 outline-none" data-id="element-3819" data-mp="irhllnn5g96n" />
          </div>
          <p className={`inline-flex items-center gap-1.5 text-xs mt-1.5 ${handleAvailable ? 'text-success' : 'text-error'}`} data-id="element-3820" data-mp="1gvuhvmk70pa">
            {handleAvailable ? <>
                <CheckCircle2Icon className="w-3.5 h-3.5" data-id="element-3821" data-mp="b7qjv8v3qgey" />
                talab.app/shop/{config.handle} is available
              </> : 'That address is taken — try another one.'}
          </p>
        </div>

        {isBusiness && <SimpleSelect label="Business Type" value={config.businessType} options={BUSINESS_TYPE_OPTIONS} onChange={businessType => update({
        businessType
      })} helper="We use this to tailor your AI assistant and suggested questions." data-id="element-3822" data-mp="g6a4n5xylm5q" />}

        <TextArea label="About your business" value={config.about} onChange={about => update({
        about
      })} placeholder="A short description customers will read on your shop." rows={4} onGenerate={writeAbout} generateLabel="Write this for me" helper="Keep it short and friendly — two or three sentences is plenty." data-id="element-3823" data-mp="2vwwbbpndylm8" />
      </Card>
    </div>;
}